import ReportIcon from '@mui/icons-material/Report';
import { LoadingButton } from '@mui/lab';
import { Button, Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ArrayTextInput } from '../components/common/ArrayTextInput';
import ConfirmActionPopup from '../components/common/ConfirmPopup';
import HintMsg from '../components/common/HintMsg';
import PageTitle from '../components/common/PageTitle';
import { PopupTableContent } from '../components/common/PopupTableContent';
import SearchSelect, { Option } from '../components/common/SearchSelect';
import SectionTitle from '../components/common/SectionTitle';
import { ParentOperatorSection } from '../components/ParentOperatorSection';
import PopupEnum from '../enum/popupEnum';
import SubLineActionTypeEnum from '../enum/subLineActionTypeEnum';
import { useError } from '../hook/useError';
import { I18nStoreType } from '../i18n/react-i18next';
import { useOperatorListStore } from '../store/operatorListStore';
import { usePopupStore } from '../store/popupStore';
import { IP_V4_MASK, IP_V6_MASK } from '../utils/regexp';

type Props = {};

export const UpdateApiWhiteListPage = (props: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [parentOp, setParentOp] = useState<Option | null>(null);
	const [targetOp, setTargetOp] = useState<Option | null>(null);
	const [parentOpError, setParentOpError] = useState<string>('');
	const [targetOpError, setTargetOpError] = useState<string>('');
	const [apiWhiteList, setApiWhiteList] = useState<string[]>([]);
	const [apiWhiteListError, setApiWhiteListError] = useState<string>('');
	const [loaded, setLoaded] = useState<boolean>(false);
	const [open, setOpen] = useState<boolean>(false);
	const { openPopup, setPopup } = usePopupStore();
	const { initOpList, opList } = useOperatorListStore();
	const { handleError } = useError();
	// handleError is re-created on every render; keep it out of the effect deps.
	const handleErrorRef = useRef(handleError);
	handleErrorRef.current = handleError;

	const goBack = () => navigate(-1);

	useEffect(() => {
		initOpList(openPopup);
	}, [initOpList, openPopup]);

	const parentFpId = parentOp?.id;
	const targetFpId = targetOp?.id;

	// The master itself can be updated too, so it heads its own list of sub lines.
	const targetOptions = useMemo(() => {
		if (!parentOp) return [];
		const subLines = opList
			.filter((op) => !op.isMasterOperator && op.parentOperator === parentOp.id)
			.map(({ fpId, name }) => ({ id: fpId, label: name }));
		return [parentOp, ...subLines];
	}, [opList, parentOp]);

	// A target picked under the previous master must not survive the switch.
	useEffect(() => {
		setTargetOp(null);
	}, [parentFpId]);

	// The approval replaces the whole list, so start from what the target has today.
	useEffect(() => {
		if (!targetFpId) {
			setLoaded(false);
			setApiWhiteList([]);
			return;
		}
		let active = true;
		(async () => {
			try {
				setLoaded(false);
				setApiWhiteListError('');
				const { operator } = await api.searchOperator({ operatorId: targetFpId });
				if (!active) return;
				setApiWhiteList(operator?.apiWhiteList || []);
				setLoaded(true);
			} catch (error: any | PopupEnum) {
				if (active) handleErrorRef.current(error);
			}
		})();
		return () => {
			active = false;
		};
	}, [targetFpId]);

	const checkEmptyError = useCallback(() => {
		setParentOpError(parentOp ? '' : 'inputErrorMsg.Required_Selected');
		setTargetOpError(targetOp ? '' : 'inputErrorMsg.Required_Selected');
		return !!parentOp && !!targetOp;
	}, [parentOp, targetOp]);

	const updateApiWhiteList = async () => {
		try {
			openPopup(PopupEnum.Loading);

			const { requisitionId } = await api.createSubLineRequisition({
				operatorName: targetOp?.label || '',
				serverUrl: '',
				apiWhiteList: apiWhiteList,
				parentOperatorId: Number(parentOp?.id),
				actionType: SubLineActionTypeEnum.Update,
				fpId: Number(targetOp?.id),
			});
			setPopup(PopupEnum.CustomMessage, `${t('management.SubLineRequisition.RequisitionId')}: ${requisitionId}`);
			resetForm();
		} catch (error: any | PopupEnum) {
			handleError(error);
		}
	};

	const resetForm = () => {
		setParentOp(null);
		setTargetOp(null);
		setApiWhiteList([]);
		setLoaded(false);
	};

	const validateIp = (ip: string) => {
		if (!(ip.match(IP_V6_MASK) || ip.match(IP_V4_MASK))) {
			setApiWhiteListError('inputErrorMsg.Valid_Ip');
			return false;
		}
		if (apiWhiteList.some((item) => item === ip)) {
			setApiWhiteListError('inputErrorMsg.Repeat_Ip');
			return false;
		}
		setApiWhiteListError('');
		return true;
	};

	const getResultMessage = () => [
		{ title: t('Master'), content: parentOp?.label || '--' },
		{ title: t('management.UpdateApiWhiteList.Target'), content: targetOp?.label || '--' },
		{ title: t('management.SubLineRequisition.ApiWhiteList'), content: apiWhiteList.join(' , ') || '--' },
	];

	const openConfirmPopup = () => {
		setApiWhiteListError('');
		if (!checkEmptyError()) return;
		setOpen(true);
	};

	return (
		<div className="space-y-4 lg:w-[700px] w-auto mx-auto">
			<ConfirmActionPopup open={open} setOpen={setOpen} comfirmAction={updateApiWhiteList}>
				<PopupTableContent messageLines={getResultMessage()} />
			</ConfirmActionPopup>
			<PageTitle content={t('management.UpdateApiWhiteList.Title')} />
			<Paper className="grid grid-cols-12 gap-4 mt-4 p-8 bg-gray-50">
				<ParentOperatorSection
					widthStyle="w-full col-span-12 md:col-span-6"
					op={parentOp}
					setOp={setParentOp}
					errorText={parentOpError ? (t(parentOpError as keyof I18nStoreType) as string) : ''}
					required
				/>
				<div className="space-y-3 w-full col-span-12 md:col-span-6">
					<SectionTitle content={t('management.UpdateApiWhiteList.Target')} required />
					<SearchSelect
						widthStyle="w-full"
						label={t('search.Select_Target_Operator')}
						option={targetOp}
						setOption={setTargetOp}
						options={targetOptions}
						disabled={!parentFpId}
						errorText={targetOpError ? (t(targetOpError as keyof I18nStoreType) as string) : ''}
					/>
				</div>

				{loaded && (
					<>
						<ArrayTextInput
							title={t('management.SubLineRequisition.ApiWhiteList')}
							list={apiWhiteList}
							validate={validateIp}
							setList={setApiWhiteList}
							emptyText={t('inputHintMsg.White_List_Empty')}
							errorText={apiWhiteListError ? (t(apiWhiteListError as keyof I18nStoreType) as string) : ''}
						/>
						<div className="col-span-12 flex items-center flex-wrap gap-1">
							<ReportIcon />
							<HintMsg content={t('inputHintMsg.White_List_Replace')} />
						</div>
					</>
				)}

				<div className="pt-8 col-span-12 flex justify-center flex-wrap gap-4">
					<Button variant="redGradient" className="color-[#ae0000]" onClick={goBack}>
						{t('management.UpdateApiWhiteList.CancelButton')}
					</Button>
					<LoadingButton variant="gradient" disabled={!loaded} onClick={openConfirmPopup}>
						{t('management.UpdateApiWhiteList.SubmitButton')}
					</LoadingButton>
				</div>
			</Paper>
		</div>
	);
};

export default UpdateApiWhiteListPage;
